import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { toastApiError } from "@/lib/errorMessages";
import {
  CalendarClock,
  ChevronRight,
  Code2,
  CreditCard,
  Info,
  Layers,
  RefreshCw,
} from "lucide-react";
import { Badge, Button, buttonVariants, EmptyState, brandFillClass } from "@/components/ui";
import {
  EntityHeaderLayout,
  PageFrame,
  SectionCard,
} from "@/components/common";
import { DefinitionList, DetailRow } from "@/components/ui";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import {
  DataTable,
  DataTablePrimaryText,
  DataTableTitle,
  type DataTableColumn,
} from "@/components/table";
import {
  adminPaymentAuditKeys,
  adminPaymentAuditService,
  type AdminAllocationLineRow,
} from "@/services/adminPaymentAuditService";
import { formatDateTime } from "@/lib/datetime";
import { formatMoney } from "@/lib/money";
import { shortId } from "./formatters";
import { enumLabel } from "@/lib/enumLabel";
import {
  economicAllocationMethodLabel,
  economicEventStatusLabel,
  economicEventTypeLabel,
} from "@/lib/economicEventLabels";
import { StatusBadge } from "./shared";
import { cn } from "@/lib/utils";

type AllocationSortColumn =
  | "step"
  | "role"
  | "amount"
  | "storedPercent"
  | "absolutePercent"
  | "ledgerAccount";

const PERCENT_MAX_DECIMALS = 4;

/** At least 1 fractional digit; strip trailing zeros; use up to 4 decimals only when needed. */
function formatPercent(
  value: number | null | undefined,
  empty: string,
): string {
  if (value == null || Number.isNaN(value)) return empty;
  const s = value.toFixed(PERCENT_MAX_DECIMALS);
  const sign = s.startsWith("-") ? "-" : "";
  const unsigned = sign ? s.slice(1) : s;
  const [intPart, fracRaw = ""] = unsigned.split(".");
  let frac = fracRaw.replace(/0+$/, "");
  if (frac.length === 0) frac = "0";
  if (frac.length > PERCENT_MAX_DECIMALS) {
    frac = frac.slice(0, PERCENT_MAX_DECIMALS);
  }
  return `${sign}${intPart}.${frac}%`;
}

const ROLE_SEGMENT_CLASS: Record<string, string> = {
  PROCESSOR_FEES: "bg-surface-400",
  SELLER: "bg-primary-500",
  HOST: "bg-success-500",
  SALES_AGENT_ACTIVITY: "bg-violet-500",
  SALES_AGENT_HOUSE: "bg-indigo-500",
  PLATFORM: "bg-accent-500",
  TREASURY_CLEARING: "bg-sky-500",
};

function roleSegmentClass(roleCode: string): string {
  return ROLE_SEGMENT_CLASS[roleCode] ?? "bg-surface-300";
}

/** Stacked bar + legend: where the gross amount went, by allocation role. */
function AllocationSplit({
  lines,
  currency,
  label,
}: {
  lines: AdminAllocationLineRow[];
  currency: string;
  label: string;
}) {
  const { t } = useTranslation("payments");
  const total = lines.reduce((sum, line) => sum + Math.max(0, line.amount_cents), 0);
  if (total <= 0) return null;
  const ordered = [...lines].sort((a, b) => a.allocation_step - b.allocation_step);
  return (
    <div className="w-full space-y-3 py-1">
      <div
        className="bg-surface-200/70 flex h-2.5 w-full overflow-hidden rounded-full"
        role="img"
        aria-label={label}
      >
        {ordered.map((line) =>
          line.amount_cents > 0 ? (
            <span
              key={line.id}
              className={cn("h-full first:rounded-l-full last:rounded-r-full", roleSegmentClass(line.role_code))}
              style={{ width: `${(line.amount_cents / total) * 100}%` }}
            />
          ) : null,
        )}
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-2">
        {ordered.map((line) => (
          <li key={line.id} className="flex items-center gap-2 text-[13px]">
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", roleSegmentClass(line.role_code))}
              aria-hidden
            />
            <span className="text-surface-600">
              {enumLabel("eventDetail.roles", line.role_code, t)}
            </span>
            <span className="text-surface-900 font-medium tabular-nums">
              {formatMoney(line.amount_cents, currency)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JsonBlock({
  title,
  noneLabel,
  data,
}: {
  title: string;
  noneLabel: string;
  data: Record<string, unknown> | null | undefined;
}) {
  const isEmpty = data == null || Object.keys(data).length === 0;
  return (
    <div className="min-w-0">
      <h3 className="text-surface-500 mb-1.5 text-xs font-medium">{title}</h3>
      {isEmpty ? (
        <p className="border-surface-200 text-surface-400 rounded-lg border border-dashed px-3 py-2.5 text-[13px]">
          {noneLabel}
        </p>
      ) : (
        <pre className="border-surface-200 bg-surface-50 text-surface-700 dark:bg-surface-100/40 overflow-x-auto rounded-lg border px-3.5 py-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AdminPaymentsEventDetailPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { eventId } = useParams<{ eventId: string }>();

  const {
    data: event,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: adminPaymentAuditKeys.event(eventId ?? ""),
    queryFn: () => {
      if (!eventId) {
        throw new Error("eventId required");
      }
      return adminPaymentAuditService.getEconomicEvent(eventId);
    },
    enabled: Boolean(eventId),
  });

  useEffect(() => {
    if (error) {
      toastApiError(error, "generic", t("eventDetail.loadError"));
    }
  }, [error, t]);

  const currency = event?.currency ?? "EUR";

  const allocationColumns = useMemo<
    DataTableColumn<AdminAllocationLineRow, AllocationSortColumn>[]
  >(
    () => [
      {
        id: "step",
        header: t("eventDetail.colStep"),
        sortKey: "step",
        sortKind: "number",
        getSortValue: (row) => row.allocation_step,
        defaultWidth: 90,
        cell: (line) => (
          <span className="tabular-nums">{line.allocation_step}</span>
        ),
      },
      {
        id: "role",
        header: t("eventDetail.colRole"),
        sortKey: "role",
        sortKind: "text",
        getSortValue: (row) => row.role_code,
        defaultWidth: 200,
        cell: (line) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", roleSegmentClass(line.role_code))}
              aria-hidden
            />
            <div className="min-w-0">
              <DataTablePrimaryText>
                {enumLabel("eventDetail.roles", line.role_code, t)}
              </DataTablePrimaryText>
              <div className="text-surface-500 font-mono text-[11px] tracking-tight">
                {line.role_code}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "amount",
        header: t("shared.colAmount"),
        sortKey: "amount",
        sortKind: "number",
        getSortValue: (row) => row.amount_cents,
        defaultWidth: 100,
        cell: (line) => (
          <span className="text-surface-900 font-medium tabular-nums">
            {formatMoney(line.amount_cents, currency)}
          </span>
        ),
      },
      {
        id: "storedPercent",
        header: t("eventDetail.colStoredPercent"),
        sortKey: "storedPercent",
        sortKind: "number",
        getSortValue: (row) => row.stored_cut_percent,
        defaultWidth: 110,
        cell: (line) => (
          <span className="text-surface-700 tabular-nums">
            {formatPercent(line.stored_cut_percent, t("shared.emptyCell"))}
          </span>
        ),
      },
      {
        id: "absolutePercent",
        header: t("eventDetail.colAbsolutePercent"),
        sortKey: "absolutePercent",
        sortKind: "number",
        getSortValue: (row) => row.absolute_cut_percent,
        defaultWidth: 100,
        cell: (line) => (
          <span className="text-surface-700 tabular-nums">
            {formatPercent(line.absolute_cut_percent, t("shared.emptyCell"))}
          </span>
        ),
      },
      {
        id: "ledgerAccount",
        header: t("eventDetail.colLedgerAccount"),
        sortKey: "ledgerAccount",
        sortKind: "text",
        getSortValue: (row) => row.ledger_account_id,
        defaultWidth: 150,
        cell: (line) => (
          <div className="flex min-w-0 flex-col items-start gap-1">
            <ClickToCopyId
              value={line.ledger_account_id}
              displayValue={shortId(line.ledger_account_id, 10)}
              className="text-surface-600 text-xs"
            />
            <span className="text-surface-400 inline-flex items-center gap-1 text-[11px] whitespace-nowrap">
              {t("eventDetail.colLineId")}
              <ClickToCopyId
                value={line.id}
                displayValue={shortId(line.id)}
                className="text-surface-500 text-[11px]"
              />
            </span>
          </div>
        ),
      },
    ],
    [currency, t],
  );

  if (isLoading && !event) {
    return (
      <PageFrame
        backLink={{
          fallback: "/admin/payments/events",
          children: t("eventDetail.allEvents"),
        }}
        isLoading
        loadingMessage={t("eventDetail.loading")}
      />
    );
  }

  if (!event) {
    return (
      <PageFrame
        backLink={{
          fallback: "/admin/payments/events",
          children: t("eventDetail.backToEvents"),
        }}
      >
        <SectionCard title={t("eventDetail.cardTitle")}>
          <EmptyState title={t("eventDetail.notFound")} />
        </SectionCard>
      </PageFrame>
    );
  }

  const extPay = event.external_payment_id;

  return (
    <PageFrame
      backLink={{
        fallback: "/admin/payments/events",
        children: t("eventDetail.allEvents"),
      }}
    >
      <EntityHeaderLayout
        className="animate-slide-up mb-0 overflow-hidden"
        icon={
          <div
            className={cn("flex h-14 w-14 items-center justify-center rounded-xl shadow-sm", brandFillClass("primary"))}
            aria-hidden
          >
            <Layers className="h-7 w-7" />
          </div>
        }
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-surface-900 text-2xl font-semibold tracking-tight text-balance sm:text-[1.75rem] sm:leading-tight">
              {economicEventTypeLabel(event.type, t)}
            </h1>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <StatusBadge
                status={event.status}
                label={economicEventStatusLabel(event.status, t)}
              />
              <Badge
                variant="outline"
                size="sm"
              >
                {economicAllocationMethodLabel(event.allocation_method, t)}
              </Badge>
              <ClickToCopyId
                value={event.id}
                displayValue={shortId(event.id, 12)}
                className="text-[11px]"
              />
            </div>
            <p className="text-surface-500 mt-3 flex items-center gap-1.5 text-[13px]">
              <CalendarClock
                className="text-surface-400 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              {formatDateTime(event.created_at)}
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:items-end">
            <div className="sm:text-right">
              <p className="text-surface-500 text-xs font-medium">
                {t("eventDetail.grossAmount")}
              </p>
              <p className="text-surface-900 mt-0.5 text-[1.75rem] leading-tight font-semibold tracking-tight tabular-nums">
                {formatMoney(event.gross_amount_cents, event.currency)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                isLoading={isFetching}
                leftIcon={<RefreshCw className="h-4 w-4" />}
                onClick={() => void refetch()}
              >
                {tCommon("refresh")}
              </Button>
              {extPay ? (
                <Link
                  to={`/admin/payments/external-payments?paymentId=${extPay}`}
                  className={buttonVariants({ variant: "primary" })}
                >
                  <CreditCard className="h-4 w-4" />
                  {t("eventDetail.viewExternalPayment")}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </EntityHeaderLayout>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start xl:gap-5">
        <div className="min-w-0 space-y-4">
          <section className="min-w-0">
            <DataTable
              title={
                <DataTableTitle
                  as="h2"
                  description={t("eventDetail.allocationLinesSubtitle", {
                    count: event.allocation_lines.length,
                  })}
                >
                  {t("eventDetail.allocationLinesTitle")}
                </DataTableTitle>
              }
              labels={tableLabels}
              closeLabel={tCommon("close")}
              loadingLabel={tCommon("loading")}
              rows={event.allocation_lines}
              columns={allocationColumns}
              getRowKey={(row) => row.id}
              defaultSortColumn="step"
              preferencesKey="event-allocation-lines"
              paginate={false}
              emptyMessage={t("eventDetail.noAllocationLines")}
              toolbar={
                event.allocation_lines.length > 0 ? (
                  <AllocationSplit
                    lines={event.allocation_lines}
                    currency={currency}
                    label={t("eventDetail.splitAria")}
                  />
                ) : undefined
              }
            />
          </section>

          <details className="group border-surface-200 bg-surface-0 shadow-card overflow-hidden rounded-xl border">
            <summary className="hover:bg-surface-50 dark:hover:bg-surface-100/50 flex cursor-pointer list-none items-center gap-3 px-5 py-3.5 transition-colors [&::-webkit-details-marker]:hidden">
              <span className="border-surface-200 bg-surface-50 text-surface-500 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border">
                <Code2 className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-surface-900 block text-sm font-semibold tracking-tight">
                  {t("eventDetail.technicalTitle")}
                </span>
                <span className="text-surface-500 block text-xs">
                  {t("eventDetail.technicalHint")}
                </span>
              </span>
              <ChevronRight
                className="text-surface-400 h-4 w-4 shrink-0 transition-transform group-open:rotate-90"
                aria-hidden
              />
            </summary>
            <div className="border-surface-200/70 grid gap-4 border-t px-5 py-4 lg:grid-cols-2">
              <JsonBlock
                title={t("eventDetail.jsonAllocationSnapshot")}
                noneLabel={t("eventDetail.jsonNone")}
                data={event.allocation_rule_snapshot}
              />
              <JsonBlock
                title={t("eventDetail.jsonMetadata")}
                noneLabel={t("eventDetail.jsonNone")}
                data={event.metadata_json}
              />
            </div>
          </details>
        </div>

        <SectionCard
          title={t("eventDetail.cardTitle")}
          icon={Info}
          className="xl:sticky xl:top-4"
        >
            <DefinitionList className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-1 [&>*]:min-w-0">
              <DetailRow label={t("eventDetail.currency")}>
                {event.currency}
              </DetailRow>
              <DetailRow
                label={t("eventDetail.idempotencyKey")}
                valueClassName="break-all text-xs text-surface-800"
              >
                {event.idempotency_key ? (
                  <ClickToCopyId
                    value={event.idempotency_key}
                    className="max-w-full text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow
                label={t("eventDetail.ledgerEntries")}
                valueClassName="tabular-nums"
              >
                {event.ledger_entry_count}
              </DetailRow>
              <DetailRow
                label={t("eventDetail.bookingId")}
                valueClassName="text-xs text-surface-800"
              >
                {event.booking_id ? (
                  <ClickToCopyId
                    value={event.booking_id}
                    className="max-w-full text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow
                label={t("eventDetail.checkoutId")}
                valueClassName="text-xs text-surface-800"
              >
                {event.checkout_id ? (
                  <ClickToCopyId
                    value={event.checkout_id}
                    className="max-w-full text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow
                label={t("eventDetail.reversesEvent")}
                valueClassName="text-xs text-surface-800"
              >
                {event.reverses_event_id ? (
                  <ClickToCopyId
                    value={event.reverses_event_id}
                    displayValue={shortId(event.reverses_event_id, 12)}
                    className="max-w-full text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow label={t("shared.colCreated")}>
                {formatDateTime(event.created_at)}
              </DetailRow>
              <DetailRow label={t("eventDetail.updated")}>
                {formatDateTime(event.updated_at)}
              </DetailRow>
            </DefinitionList>
        </SectionCard>
      </div>
    </PageFrame>
  );
}
